/**
 * bossRam.ts
 * Ram-charge state machine for the Void Mothership.
 *
 * Sibling of `bossSystem.ts` — extracted so the kinematic logic lives
 * next to `bossTurret.ts` / `bossDroneSpawner.ts` instead of bloating
 * the entity or orchestrator past the 300-line file budget.
 *
 * Drives the `RamPhase` sub-state-machine while the top-level boss
 * state is `HOLD`:
 *
 *     IDLE ── ramCooldown ≤ 0 && !isShielded ──▶ TELEGRAPH
 *     TELEGRAPH ── ramPhaseTimer ≤ 0          ──▶ CHARGE  (vx = −SPEED, distance = DIST)
 *     CHARGE ── ramDistanceRemaining ≤ 0      ──▶ RETURN  (vx = +SPEED)
 *     RETURN ── x ≥ cameraX + holdOffset      ──▶ IDLE    (reload cooldown)
 *
 * Why "!isShielded" gate? The shield window is the player's read of
 * "incoming danger" — kicking off a ram while the shield is up would
 * stack two unavoidable threats. Gating the windup on the expose
 * window keeps the cycle parsable.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * @module bossRam
 */

import { spaceState } from './spaceState';
import { spawnExplosion } from './explosionVfx';
import { triggerSpaceShake } from './cameraShake';
import type { VoidMothership } from '../entities/VoidMothership';
import Logger from '../utils/logger';
import {
  VOID_MOTHERSHIP_HOLD_X_OFFSET,
  VOID_MOTHERSHIP_RAM_COOLDOWN_S,
  VOID_MOTHERSHIP_RAM_TELEGRAPH_S,
  VOID_MOTHERSHIP_RAM_SPEED,
  VOID_MOTHERSHIP_RAM_DISTANCE,
  VOID_MOTHERSHIP_RAM_TRAIL_INTERVAL_S,
} from '../../constants/bossConstants';
import {
  SPACE_SHAKE_DURATION_BOSS_RAM_S,
  SPACE_SHAKE_MAGNITUDE_BOSS_RAM_PX,
} from '../../constants/spaceConstants';

/**
 * Advance the boss's ram sub-state-machine one fixed timestep.
 *
 * Safe no-op when the boss is not in HOLD or is dead — callers (e.g.
 * `updateBoss`) don't have to gate on state themselves.
 *
 * @param boss - The live boss entity (caller ensures non-null).
 * @param dt - Fixed timestep in seconds.
 */
export function stepBossRam(boss: VoidMothership, dt: number): void {
  if (boss.dead || boss.state !== 'HOLD') return;

  switch (boss.ramPhase) {
    case 'IDLE': {
      // Hold the windup while the shield is up so the ram doesn't stack
      // on top of an unavoidable defence window.
      if (boss.isShielded) return;
      boss.ramCooldown -= dt;
      if (boss.ramCooldown <= 0) {
        boss.ramPhase = 'TELEGRAPH';
        Logger.debug('VoidMothershipRam', 'IDLE → TELEGRAPH');
        boss.ramPhaseTimer = VOID_MOTHERSHIP_RAM_TELEGRAPH_S;
      }
      return;
    }
    case 'TELEGRAPH': {
      boss.ramPhaseTimer -= dt;
      if (boss.ramPhaseTimer <= 0) {
        boss.ramPhase = 'CHARGE';
        Logger.debug('VoidMothershipRam', 'TELEGRAPH → CHARGE');
        boss.vx = -VOID_MOTHERSHIP_RAM_SPEED;
        boss.ramDistanceRemaining = VOID_MOTHERSHIP_RAM_DISTANCE;
        // Ignition burst at the boss's leading (left) edge so the
        // player reads CHARGE the same frame the kinematic state
        // flips. Seed the trail timer to 0 so the first CHARGE tick
        // drops a trail burst at the boss centre instead of waiting
        // a full interval.
        // DECISION: spawn the ignition burst here (state-transition
        // site) rather than in the CHARGE branch on `ramDistance ==
        // RAM_DISTANCE`; the latter would mis-fire if a future
        // tweak ever resets `ramDistanceRemaining` mid-CHARGE.
        spawnExplosion(boss.x, boss.y + boss.height / 2);
        // DECISION: pair the ignition VFX with a camera shake so the
        // ram telegraph has physical weight in addition to the visual
        // burst. Triggered at the same state-transition site so the
        // VFX and shake fire on exactly the same tick — never one
        // without the other. `triggerSpaceShake` will defer to a
        // louder in-flight shake (e.g. the player just took a hit),
        // so this can't drown out heavier feedback.
        triggerSpaceShake(
          SPACE_SHAKE_MAGNITUDE_BOSS_RAM_PX,
          SPACE_SHAKE_DURATION_BOSS_RAM_S,
        );
        boss.ramTrailTimer = 0;
      }
      return;
    }
    case 'CHARGE': {
      // DECISION: integrate by |vx*dt| against the remaining-distance
      // counter rather than tracking world-space start-x. Keeps the
      // CHARGE leg agnostic of camera scroll during the window.
      const step = VOID_MOTHERSHIP_RAM_SPEED * dt;
      boss.x += boss.vx * dt;
      boss.ramDistanceRemaining -= step;
      // Trail VFX: drop bursts at the boss's geometric centre on a
      // fixed cadence so the exhaust trail spacing reads consistently
      // regardless of dt jitter. Cadence + pool budget rationale
      // lives on `VOID_MOTHERSHIP_RAM_TRAIL_INTERVAL_S`.
      // DECISION: accumulate leftover with `+= INTERVAL` and loop
      // rather than `= INTERVAL` reset. The reset form discards
      // overshoot, so a long hitch (`dt > INTERVAL`) would drop at
      // most one burst and drift the cadence; the loop form catches
      // up on the next tick and keeps spacing dt-independent. The
      // `while` is bounded by the explosion pool — `spawnExplosion`
      // is a silent no-op on saturation (AGENTS.md pool rule).
      boss.ramTrailTimer -= dt;
      while (boss.ramTrailTimer < 0) {
        spawnExplosion(boss.x + boss.width / 2, boss.y + boss.height / 2);
        boss.ramTrailTimer += VOID_MOTHERSHIP_RAM_TRAIL_INTERVAL_S;
      }
      if (boss.ramDistanceRemaining <= 0) {
        boss.ramPhase = 'RETURN';
        Logger.debug('VoidMothershipRam', 'CHARGE → RETURN (ram distance spent)');
        boss.vx = VOID_MOTHERSHIP_RAM_SPEED;
      }
      return;
    }
    case 'RETURN': {
      boss.x += boss.vx * dt;
      const anchor = spaceState.cameraX + VOID_MOTHERSHIP_HOLD_X_OFFSET;
      if (boss.x >= anchor) {
        // Snap to anchor so the entity's own camera-lock can resume on
        // the next tick without a one-frame overshoot.
        boss.x = anchor;
        boss.vx = 0;
        boss.ramPhase = 'IDLE';
        boss.ramCooldown = VOID_MOTHERSHIP_RAM_COOLDOWN_S;
        Logger.debug('VoidMothershipRam', 'RETURN → IDLE (back at hold anchor)');
      }
      return;
    }
  }
}
